/**
 * Borrador del formulario de entrevista: puro, sin React, para poder testear la
 * regla de qué se manda al servidor sin renderizar nada.
 *
 * La regla es que **se manda solo lo que cambió**. El endpoint acepta guardado
 * parcial (`min(1)` respuesta), así que mandar las 15 en cada guardado escribiría
 * quince filas de las que catorce no se tocaron, y pisaría con `''` respuestas
 * que otra persona cargó entre que se abrió el formulario y se apretó Guardar.
 */
import type { InterviewAnswer, InterviewBlock } from '@ops/contracts'

/** Clave estable de una pregunta dentro del borrador: `A-1`. */
export function claveDe(bloque: InterviewBlock, preguntaNro: number): string {
  return `${bloque}-${preguntaNro}`
}

export function parsearClave(
  clave: string,
): { bloque: InterviewBlock; preguntaNro: number } | null {
  const partes = clave.split('-')
  const bloque = partes[0]
  const nro = Number(partes[1])
  if (partes.length !== 2 || bloque === undefined) return null
  if (!Number.isInteger(nro)) return null
  if (!['A', 'B', 'C', 'D', 'E'].includes(bloque)) return null
  return { bloque: bloque as InterviewBlock, preguntaNro: nro }
}

export type Borrador = Record<string, string>

/** Estado inicial del formulario: lo que ya está guardado. */
export function borradorDe(respuestas: readonly InterviewAnswer[]): Borrador {
  const salida: Borrador = {}
  for (const r of respuestas) {
    salida[claveDe(r.bloque, r.preguntaNro)] = r.respuesta
  }
  return salida
}

export interface RespuestaCambiada {
  bloque: InterviewBlock
  preguntaNro: number
  respuesta: string
}

/**
 * Diferencia entre el borrador y lo guardado, en el orden del cuestionario.
 * Vacío = no hay nada que guardar y el botón queda deshabilitado.
 */
export function cambiosDe(
  borrador: Borrador,
  original: Readonly<Borrador>,
): RespuestaCambiada[] {
  const salida: RespuestaCambiada[] = []

  for (const [clave, valor] of Object.entries(borrador)) {
    if (valor === (original[clave] ?? '')) continue
    const parsed = parsearClave(clave)
    if (parsed === null) continue
    salida.push({ ...parsed, respuesta: valor })
  }

  return salida.sort(
    (a, b) =>
      a.bloque.localeCompare(b.bloque) || a.preguntaNro - b.preguntaNro,
  )
}

/** Cuántas preguntas del borrador tienen texto. Se muestra en vivo. */
export function respondidasDe(borrador: Readonly<Borrador>): number {
  return Object.values(borrador).filter((valor) => valor.trim() !== '').length
}
